// Storage wrapper that namespaces all keys behind a widget slug.
// Convention only — defends against accidental cross-widget collisions
// when widgets share a host origin (e.g. same iframe sandbox setup).

export interface PrefixedStorage {
  get<T = unknown>(key: string): T | null
  set(key: string, value: unknown): void
  remove(key: string): void
  clear(): void
}

const noopStorage: PrefixedStorage = {
  get: () => null,
  set: () => {},
  remove: () => {},
  clear: () => {},
}

export function usePrefixedStorage(
  prefix: string,
  backend: 'session' | 'local' = 'session',
): PrefixedStorage {
  if (typeof window === 'undefined') return noopStorage

  const store = backend === 'local' ? window.localStorage : window.sessionStorage
  const fullKey = (key: string) => `${prefix}:${key}`

  return {
    get<T>(key: string): T | null {
      try {
        const raw = store.getItem(fullKey(key))
        return raw === null ? null : (JSON.parse(raw) as T)
      } catch {
        return null
      }
    },
    set(key: string, value: unknown): void {
      try {
        store.setItem(fullKey(key), JSON.stringify(value))
      } catch {
        // quota exceeded or storage unavailable — silent
      }
    },
    remove(key: string): void {
      try {
        store.removeItem(fullKey(key))
      } catch {
        // ignore
      }
    },
    clear(): void {
      try {
        const keys: string[] = []
        for (let i = 0; i < store.length; i++) {
          const k = store.key(i)
          if (k && k.startsWith(`${prefix}:`)) keys.push(k)
        }
        keys.forEach((k) => store.removeItem(k))
      } catch {
        // ignore
      }
    },
  }
}
